// Cliente HTTP mínimo contra el backend. La web se sirve en el mismo
// origen que la API (dimensionado.satfilters.es), así que la cookie de
// sesión viaja sola; aun así forzamos `credentials: 'include'`.
import type { EstadoFicha, FichaInput, ArchivoFicha } from '@dim/shared';

export interface Usuario {
  id: number;
  email: string;
  nombre: string;
  rol: 'admin' | 'gestor_oficina' | 'tecnico';
}

/** Fila del listado de fichas. */
export interface FichaResumen {
  id: number;
  estado: EstadoFicha;
  clienteNombre: string;
  clienteTelefono: string;
  tecnicoId: number;
  tecnicoNombre: string;
  archivosTotal: number;
  archivosSubidos: number;
  createdAt: string;
  updatedAt: string;
}

/** Ficha completa (detalle). */
export interface FichaDetalle {
  id: number;
  estado: EstadoFicha;
  data: FichaInput;
  tecnicoId: number;
  tecnicoNombre: string;
  onedriveFolderUrl: string | null;
  createdAt: string;
  updatedAt: string;
  archivos: ArchivoFicha[];
}

/** Usuario tal como lo lista la gestión (solo admin). */
export interface UsuarioAdmin {
  id: number;
  email: string;
  nombre: string;
  rol: Usuario['rol'];
  activo: boolean;
  tieneApiKey: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface NuevoUsuario {
  email: string;
  nombre: string;
  rol: Usuario['rol'];
  password: string;
}

export interface EdicionUsuario {
  nombre?: string;
  rol?: Usuario['rol'];
  activo?: boolean;
}

/** Configuración tal como la devuelve GET /api/config (secretos enmascarados). */
export interface ConfigData {
  empresa: { nombre: string; cif: string; telefono: string; email: string; direccion: string };
  graph: {
    tenantId: string;
    clientId: string;
    clientSecretSet: boolean;
    rootPath: string;
    driveId: string;
  };
  smtp: {
    host: string;
    port: number;
    secure: boolean;
    user: string;
    passwordSet: boolean;
    from: string;
    avisoTo: string;
  };
}

/** Cuerpo de PUT /api/config (todo opcional; secretos vacíos no se tocan). */
export interface ConfigGuardar {
  empresa?: Partial<ConfigData['empresa']>;
  graph?: Partial<Omit<ConfigData['graph'], 'clientSecretSet'>> & { clientSecret?: string };
  smtp?: Partial<Omit<ConfigData['smtp'], 'passwordSet'>> & { password?: string };
}

export class ApiError extends Error {
  constructor(public status: number, message: string, public body?: unknown) {
    super(message);
    this.name = 'ApiError';
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`/api${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(init?.headers ?? {}) },
    ...init,
  });
  const data: unknown = await res.json().catch(() => ({}));
  if (!res.ok) {
    const msg = (data as { error?: string })?.error ?? `HTTP ${res.status}`;
    throw new ApiError(res.status, msg, data);
  }
  return data as T;
}

export interface FichasFiltro {
  estado?: EstadoFicha;
  q?: string;
  tecnico?: number;
}

export const api = {
  me: () => request<{ user: Usuario }>('/auth/me'),
  login: (email: string, password: string) =>
    request<{ user: Usuario }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
    }),
  logout: () => request<{ ok: true }>('/auth/logout', { method: 'POST' }),

  listFichas: (filtro: FichasFiltro = {}) => {
    const qs = new URLSearchParams();
    if (filtro.estado) qs.set('estado', filtro.estado);
    if (filtro.q) qs.set('q', filtro.q);
    if (filtro.tecnico) qs.set('tecnico', String(filtro.tecnico));
    const s = qs.toString();
    return request<{ fichas: FichaResumen[] }>(`/fichas${s ? `?${s}` : ''}`);
  },
  getFicha: (id: number) => request<{ ficha: FichaDetalle }>(`/fichas/${id}`),
  setEstado: (id: number, estado: EstadoFicha) =>
    request<{ ok: true }>(`/fichas/${id}`, {
      method: 'PATCH',
      body: JSON.stringify({ estado }),
    }),

  // ---- Gestión de usuarios (solo admin) ----
  listUsuarios: () => request<{ usuarios: UsuarioAdmin[] }>('/usuarios'),
  crearUsuario: (input: NuevoUsuario) =>
    request<{ id: number; email: string; nombre: string; rol: Usuario['rol']; apiKey: string | null }>(
      '/usuarios',
      { method: 'POST', body: JSON.stringify(input) },
    ),
  editarUsuario: (id: number, cambios: EdicionUsuario) =>
    request<{ ok: true }>(`/usuarios/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(cambios),
    }),
  resetPassword: (id: number, password: string) =>
    request<{ ok: true }>(`/usuarios/${id}/password`, {
      method: 'POST',
      body: JSON.stringify({ password }),
    }),
  regenerarApiKey: (id: number) =>
    request<{ apiKey: string }>(`/usuarios/${id}/api-key`, { method: 'POST' }),

  // ---- Perfil propio ----
  actualizarPerfil: (nombre: string, email: string) =>
    request<{ user: Usuario }>('/auth/perfil', {
      method: 'PATCH',
      body: JSON.stringify({ nombre, email }),
    }),
  cambiarPassword: (actual: string, nueva: string) =>
    request<{ ok: true }>('/auth/password', {
      method: 'POST',
      body: JSON.stringify({ actual, nueva }),
    }),

  // ---- Configuración (solo admin) ----
  getConfig: () => request<ConfigData>('/config'),
  guardarConfig: (cambios: ConfigGuardar) =>
    request<{ ok: true }>('/config', { method: 'PUT', body: JSON.stringify(cambios) }),
};

/** Etiqueta legible para cada rol. */
export const ROL_LABEL: Record<Usuario['rol'], string> = {
  admin: 'Administrador',
  gestor_oficina: 'Gestor de oficina',
  tecnico: 'Técnico',
};

/** Etiqueta legible para cada estado de ficha. */
export const ESTADO_LABEL: Record<EstadoFicha, string> = {
  borrador: 'Borrador',
  recibida: 'Recibida',
  en_dimensionado: 'En dimensionado',
  presupuestada: 'Presupuestada',
  aceptada: 'Aceptada',
  rechazada: 'Rechazada',
  archivada: 'Archivada',
};

/** Formatea una fecha ISO a dd/mm/aaaa hh:mm (es-ES). */
export function fechaCorta(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString('es-ES', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
